import React, { useState, useEffect } from 'react';
import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { useNumberFormatter } from '@guest-widgets/shared/src/hooks/useNumberFormatter';
import {
  ScaleBreakpoint,
  scaleWithBreakpoints,
} from '@guest-widgets/shared/src/utils/scaleWithBreakpoints';
import { styled } from '@guest-widgets/core';

import { useFilters } from '../../filtersContext/filtersContext';

// Breakpoints are manually picked as by design.
// X values are chosen to be from 0 to 100 for simplicity and represent the position of the thumb
//  on a slider in percents.
const scaleBreakpoints: ScaleBreakpoint[] = [
  [0, 15],
  [30, 50],
  [50, 100],
  [70, 200],
  [100, 500],
];
const markPoints = [0, 100];
const { scale, descale } = scaleWithBreakpoints(scaleBreakpoints);

export interface RadiusFilterProps {
  ariaLabelledBy: string;
  className?: string;
}

export const RadiusFilter = ({ ariaLabelledBy, className }: RadiusFilterProps) => {
  const {
    filters: { location },
    setLocation,
  } = useFilters();
  const { formatKilometers } = useNumberFormatter();
  const [sliderValue, setSliderValue] = useState(location ? descale(location.radius) : 0);

  useEffect(() => {
    if (location) setSliderValue(descale(location.radius));
  }, [location]);

  if (!location) return null;

  const handleChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue !== 'number') return;
    setSliderValue(newValue);
  };

  const handleChangeCommitted = (
    event: React.SyntheticEvent | Event,
    newValue: number | number[]
  ) => {
    if (typeof newValue !== 'number' || !location) return;
    setLocation({ ...location, radius: Math.round(scale(newValue)) });
  };

  const min = scaleBreakpoints[0][0];
  const max = scaleBreakpoints[scaleBreakpoints.length - 1][0];
  const marks = markPoints.map((value) => ({
    value,
    label: <Typography variant="caption">{formatKilometers(scale(value))}</Typography>,
  }));

  return (
    <Box className={className} px={5} pt={10}>
      <StyledSlider
        color="secondary"
        size="small"
        valueLabelDisplay="on"
        value={sliderValue}
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
        valueLabelFormat={(km) => formatKilometers(km)}
        getAriaValueText={formatKilometers}
        aria-labelledby={ariaLabelledBy}
        scale={scale}
        min={min}
        max={max}
        marks={marks}
      />
    </Box>
  );
};

const StyledSlider = styled(Slider)(({ widgetArea: { basedOnSize } }) => ({
  '& .MuiSlider-markLabel': {
    top: basedOnSize(40, 24, 24),
  },
}));
